import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Navigate, Route, Routes } from 'react-router';
import { BrowserRouter } from 'react-router-dom';
import MomentFormats from './Common/Helper/MomentFormats';
import { Login } from './Welcome/Components/Login';
import { Settings } from './SettingsSection/Settings';
import { ProfileSection } from './SettingsSection/SubSections/ProfileSection';
import { SecuritySection } from './SettingsSection/SubSections/SecuritySection';
import { AppearanceSection } from './SettingsSection/SubSections/AppearanceSection';
import { RolesSection } from './SettingsSection/SubSections/RolesSection';
import { UsersAdminSection } from './SettingsSection/SubSections/UsersAdminSection';
import { UserMappingsAdminSection } from './SettingsSection/SubSections/UserMappingsAdminSection';
import { RolesAdminSection } from './SettingsSection/SubSections/RolesAdminSection';
import { RoleMembersAdminSection } from './SettingsSection/SubSections/RoleMembersAdminSection';
import { IntegrationsAdminSection } from './SettingsSection/SubSections/IntegrationsSettingsSection';
import { UserMappings } from './UserManagement/UserMappings';
import { PageAccessMap } from './Menus';
import { CompartmentDetails } from './CompartmentManagement/CompartmentDetails';
import { OperationGroupDetails } from './OperationGroups/OperationGroupDetails';
import { BlueprintManagement } from './BlueprintManagement/BlueprintManagement';
import { AreaManagement } from './AreaManagement/AreaManagement';
import { AreaUsers } from './AreaManagement/AreaUsers';
import { NoPermissionsContent } from './Welcome/Components/NoPermissionsContent';
import { UpdateAvailableBanner } from './Common/Components/UpdateAvailableBanner';
import { useMemo } from 'react';
import { Logout } from './Welcome/Components/Logout';
import { DeviceIntegrationsProvider } from './DeviceIntegrationManagement/Providers/DeviceIntegrationProvider';

export const EslManager = (): React.JSX.Element => {
  const menuRoutes = useMemo(
    () =>
      Object.entries(PageAccessMap)
        .filter(([, pa]) => pa.disabled !== true)
        .map(([title, pageAccess], index) => <Route key={'route' + index + '-' + title} {...pageAccess.route} />),
    [],
  );
  return (
    <LocalizationProvider dateAdapter={AdapterMoment} dateFormats={MomentFormats}>
      <BrowserRouter>
        <UpdateAvailableBanner />
        <Routes>
          {/* Routes WITHOUT menu entry: */}
          <Route path="/" element={<NoPermissionsContent />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/login" element={<Login />} />
          <Route path="/me" element={<Settings />}>
            <Route index element={<Navigate to="/me/profile" replace />} />
            <Route path="profile" element={<ProfileSection />} />
            <Route path="security" element={<SecuritySection />} />
            <Route path="appearance" element={<AppearanceSection />} />
            <Route path="roles" element={<RolesSection />} />
            <Route path="admin/users" element={<UsersAdminSection />} />
            <Route path="admin/users/:userId/mappings" element={<UserMappingsAdminSection />} />
            <Route path="admin/roles" element={<RolesAdminSection />} />
            <Route path="admin/roles/:roleId/users" element={<RoleMembersAdminSection />} />
            <Route
              path="admin/integrations"
              element={
                <DeviceIntegrationsProvider>
                  <IntegrationsAdminSection />
                </DeviceIntegrationsProvider>
              }
            />
          </Route>
          {/* Parametrized routes: */}
          <Route path="/mappings/:entityType/:entityId" element={<UserMappings />} />
          <Route path="/compartments/:compartmentidentifier" element={<CompartmentDetails />} />
          <Route path="/operationgroups/:operationgroupid" element={<OperationGroupDetails />} />
          <Route path="/picking/blueprints/:zoneName/:coordinate" element={<BlueprintManagement />} />
          <Route path="/areas/:id" element={<AreaManagement />} />
          <Route path="/areas/:id/users" element={<AreaUsers />} />
          {/* Routes from menu entry: */}
          {menuRoutes}
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </LocalizationProvider>
  );
};
